/**
 * Криптографический сервис для генерации JWT с подписью Ed25519
 * Используем tweetnacl - готовую библиотеку без необходимости настройки SHA512
 * 
 * Поддерживает два формата токенов:
 * 1. JWT (legacy, 320 байт)
 * 2. Binary (новый, 75 байт)
 */
import nacl from 'tweetnacl'
import {
  generateBinaryToken,
  type BinaryTokenPayload
} from '../utils/binaryToken'

// Кеш для keypair (оптимизация производительности)
let cachedKeyPair: { privateKeyHex: string, keyPair: nacl.SignKeyPair } | null = null

console.log('[CryptoService] Using tweetnacl for Ed25519 signatures')

export interface QRTokenPayload {
  employee_id: string
  role: 'worker' | 'admin'
  ts: number // timestamp (time slot, округлённый до 10 секунд)
  nonce?: string
  iat: number // issued at
}

/**
 * Конвертирует hex строку в Uint8Array
 */
function hexToBytes(hex: string): Uint8Array {
  if (!hex || typeof hex !== 'string') {
    throw new Error('Hex строка должна быть непустой строкой')
  }
  
  // Проверяем, что длина четная
  if (hex.length % 2 !== 0) {
    throw new Error('Hex строка должна иметь четную длину')
  }
  
  // Проверяем, что строка содержит только hex символы
  if (!/^[0-9a-fA-F]+$/.test(hex)) {
    throw new Error('Hex строка содержит невалидные символы')
  }
  
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    const byteValue = parseInt(hex.substring(i, i + 2), 16)
    if (isNaN(byteValue)) {
      throw new Error(`Невалидный hex байт на позиции ${i}`)
    }
    bytes[i / 2] = byteValue
  }
  return bytes
}

/**
 * Конвертирует Uint8Array в hex строку
 */
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Конвертирует Uint8Array в base64url
 */
function base64UrlEncode(bytes: Uint8Array): string {
  // Конвертируем в base64
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)
  
  // Конвертируем в base64url
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export class CryptoService {
  /**
   * Генерирует JWT токен с подписью Ed25519
   */
  static async generateJWT(
    payload: QRTokenPayload,
    privateKeyHex: string
  ): Promise<string> {
    try {
      console.log('[JWT Generation] Starting...', {
        payloadKeys: Object.keys(payload),
        privateKeyLength: privateKeyHex?.length,
        privateKeyFormat: /^[0-9a-fA-F]+$/.test(privateKeyHex) ? 'hex' : 'invalid'
      })
      
      // Валидация payload
      if (!payload || typeof payload !== 'object') {
        throw new Error('Payload должен быть объектом')
      }
      
      if (!payload.employee_id || typeof payload.employee_id !== 'string') {
        throw new Error('employee_id должен быть непустой строкой')
      }
      
      if (payload.role !== 'worker' && payload.role !== 'admin') {
        throw new Error('role должен быть "worker" или "admin"')
      }
      
      // Валидация приватного ключа
      if (!CryptoService.validatePrivateKeyHex(privateKeyHex)) {
        console.error('[JWT Generation] Invalid private key format:', {
          length: privateKeyHex?.length,
          isHex: /^[0-9a-fA-F]+$/.test(privateKeyHex),
          firstChars: privateKeyHex?.substring(0, 8)
        })
        throw new Error('Невалидный формат приватного ключа')
      }
      
      // Конвертируем приватный ключ из hex в Uint8Array
      let privateKeyBytes: Uint8Array
      try {
        privateKeyBytes = hexToBytes(privateKeyHex)
        console.log('[JWT Generation] Private key converted:', {
          length: privateKeyBytes.length,
          expected: 32
        })
      } catch (error) {
        console.error('[JWT Generation] hexToBytes failed:', error)
        throw new Error('Не удалось конвертировать ключ из hex')
      }
      
      // Создаём JWT header
      const header = {
        alg: 'EdDSA',
        typ: 'JWT'
      }
      
      // Кодируем header и payload в base64url
      const headerB64 = base64UrlEncode(
        new TextEncoder().encode(JSON.stringify(header))
      )
      const payloadB64 = base64UrlEncode(
        new TextEncoder().encode(JSON.stringify(payload))
      )
      
      console.log('[JWT Generation] Encoded parts:', {
        headerLength: headerB64.length,
        payloadLength: payloadB64.length
      })
      
      // Формируем сообщение для подписи: header.payload
      const message = `${headerB64}.${payloadB64}`
      const messageBytes = new TextEncoder().encode(message)
      
      // Подписываем сообщение Ed25519 используя tweetnacl
      let signatureBytes: Uint8Array
      try {
        // Используем кешированный keypair для ускорения
        if (!cachedKeyPair || cachedKeyPair.privateKeyHex !== privateKeyHex) {
          // Генерируем keypair только при смене ключа
          cachedKeyPair = {
            privateKeyHex,
            keyPair: nacl.sign.keyPair.fromSeed(privateKeyBytes)
          }
        }
        
        // Подписываем сообщение (быстрая операция)
        signatureBytes = nacl.sign.detached(messageBytes, cachedKeyPair.keyPair.secretKey)
      } catch (error) {
        console.error('[JWT Generation] sign() failed:', error)
        throw new Error('Не удалось создать подпись Ed25519: ' + String(error))
      }
      
      // Кодируем подпись в base64url
      const signatureB64 = base64UrlEncode(signatureBytes)
      
      // Формируем итоговый JWT: header.payload.signature
      const jwt = `${headerB64}.${payloadB64}.${signatureB64}`
      console.log('[JWT Generation] SUCCESS! JWT length:', jwt.length)
      return jwt
    } catch (error) {
      console.error('[JWT Generation] ERROR:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Ошибка генерации JWT токена')
    }
  }
  
  /**
   * Генерирует payload для QR-кода
   */
  static generateQRPayload(
    employeeId: string,
    role: 'worker' | 'admin',
    timeSlot?: number
  ): QRTokenPayload {
    // Валидация входных данных
    if (!employeeId || typeof employeeId !== 'string') {
      throw new Error('employeeId должен быть непустой строкой')
    }
    
    if (role !== 'worker' && role !== 'admin') {
      throw new Error('role должен быть "worker" или "admin"')
    }
    
    const now = Math.floor(Date.now() / 1000)
    
    // Генерируем ТЕКУЩИЙ time slot
    // QR валиден с ts до ts+5 секунд (TTL = 5 секунд для безопасности)
    const ts = timeSlot || Math.floor(now / 5) * 5
    
    // Проверяем, что timeSlot не в будущем и не слишком старый (макс 1 час назад)
    if (ts > now + 60 || ts < now - 3600) {
      throw new Error('Невалидный timeSlot')
    }
    
    // Генерируем nonce для уникальности
    const nonce = Math.random().toString(36).substring(2, 15)
    
    return {
      employee_id: employeeId,
      role,
      ts,
      nonce,
      iat: ts
    }
  }
  
  /**
   * Декодирует приватный ключ из онбординг QR
   * Формат: JSON с полями employee_id, private_key (hex), role, token
   */
  static decodeOnboardingQR(qrData: string): {
    employee_id: string
    private_key: string
    role: 'worker' | 'admin'
    token: string
  } | null {
    try {
      const data = JSON.parse(qrData)
      
      if (!data.employee_id || !data.private_key || !data.role || !data.token) {
        return null
      }
      
      return {
        employee_id: data.employee_id,
        private_key: data.private_key,
        role: data.role,
        token: data.token
      }
    } catch (error) {
      console.error('Failed to decode onboarding QR:', error)
      return null
    }
  }
  
  /**
   * Валидирует формат приватного ключа (hex, 64 символа для Ed25519)
   */
  static validatePrivateKeyHex(privateKeyHex: string): boolean {
    if (!privateKeyHex || typeof privateKeyHex !== 'string') {
      return false
    }
    // Ed25519 приватный ключ - 32 байта = 64 hex символа
    return /^[0-9a-fA-F]{64}$/.test(privateKeyHex)
  }
  
  /**
   * Простое шифрование приватного ключа (для хранения)
   * В продакшне использовать более безопасное шифрование
   */
  static encryptPrivateKey(privateKeyHex: string, password: string): string {
    // Валидация ключа перед шифрованием
    if (!CryptoService.validatePrivateKeyHex(privateKeyHex)) {
      throw new Error('Невалидный формат приватного ключа')
    }
    
    // Простое XOR шифрование (для MVP)
    // В продакшне использовать Web Crypto API с AES-GCM
    const keyBytes = hexToBytes(privateKeyHex)
    const passwordBytes = new TextEncoder().encode(password)
    
    const encrypted = new Uint8Array(keyBytes.length)
    for (let i = 0; i < keyBytes.length; i++) {
      encrypted[i] = keyBytes[i] ^ passwordBytes[i % passwordBytes.length]
    }
    
    return bytesToHex(encrypted)
  }
  
  /**
   * Расшифровка приватного ключа
   */
  static decryptPrivateKey(encryptedHex: string, password: string): string {
    console.log('[Decrypt] Starting...', {
      encryptedLength: encryptedHex?.length,
      passwordLength: password?.length
    })
    
    if (!encryptedHex || typeof encryptedHex !== 'string') {
      throw new Error('Невалидный формат зашифрованного ключа')
    }
    
    let encryptedBytes: Uint8Array
    try {
      encryptedBytes = hexToBytes(encryptedHex)
      console.log('[Decrypt] Encrypted bytes:', {
        length: encryptedBytes.length,
        expected: 32
      })
    } catch (error) {
      console.error('[Decrypt] hexToBytes failed:', error)
      throw new Error('Не удалось конвертировать зашифрованный ключ')
    }
    
    const passwordBytes = new TextEncoder().encode(password)
    
    const decrypted = new Uint8Array(encryptedBytes.length)
    for (let i = 0; i < encryptedBytes.length; i++) {
      decrypted[i] = encryptedBytes[i] ^ passwordBytes[i % passwordBytes.length]
    }
    
    const decryptedHex = bytesToHex(decrypted)
    
    console.log('[Decrypt] Decrypted:', {
      length: decryptedHex.length,
      expected: 64,
      isValid: CryptoService.validatePrivateKeyHex(decryptedHex),
      firstChars: decryptedHex.substring(0, 8)
    })
    
    // Валидация расшифрованного ключа
    if (!CryptoService.validatePrivateKeyHex(decryptedHex)) {
      console.error('[Decrypt] Validation FAILED')
      throw new Error('Не удалось расшифровать приватный ключ. Возможно, данные повреждены.')
    }
    
    console.log('[Decrypt] SUCCESS')
    return decryptedHex
  }
  
  /**
   * ============================================
   * НОВЫЙ ФОРМАТ: Бинарные токены (75 байт)
   * ============================================
   */
  
  /**
   * Генерирует бинарный JWT токен (новый формат, 75 байт)
   * @param employeeUuid - UUID сотрудника
   * @param role - роль сотрудника
   * @param privateKeyHex - приватный ключ в hex формате (64 символа)
   * @param shortId - short_id сотрудника (если известен)
   * @returns hex строка (150 символов)
   */
  static async generateBinaryJWT(
    employeeUuid: string,
    role: 'worker' | 'admin',
    privateKeyHex: string,
    shortId?: number
  ): Promise<string> {
    console.log('[BinaryJWT] Generating binary token...', {
      uuidLength: employeeUuid.length,
      role,
      shortId
    })
    
    // Валидация приватного ключа
    if (!this.validatePrivateKeyHex(privateKeyHex)) {
      throw new Error('Невалидный формат приватного ключа')
    }
    
    // Используем shortId если передан, иначе генерируем временный из UUID
    let employee_short_id: number
    if (shortId !== undefined && shortId !== null) {
      employee_short_id = shortId
      console.log(`[BinaryJWT] Using provided short_id: ${employee_short_id}`)
    } else {
      // Временное решение: конвертируем первые 8 символов UUID в число
      const uuidPrefix = employeeUuid.replace(/-/g, '').substring(0, 8)
      employee_short_id = parseInt(uuidPrefix, 16) & 0xFFFFFFFF // uint32
      console.log(`[BinaryJWT] Generated temporary short_id from UUID: ${employee_short_id}`)
    }
    
    // Генерируем timestamp (округлённый до 5 секунд - TTL QR кода)
    const now = Math.floor(Date.now() / 1000)
    const timestamp = Math.floor(now / 5) * 5
    
    console.log('[BinaryJWT] Timestamp:', {
      now,
      rounded: timestamp,
      diff: now - timestamp
    })
    
    // Формируем payload
    const payload: BinaryTokenPayload = {
      employee_short_id,
      timestamp,
      role,
      nonce: 0 // будет сгенерирован в generateBinaryToken
    }
    
    // Создаём токен
    const tokenHex = await generateBinaryToken(payload, privateKeyHex)
    
    console.log(`[BinaryJWT] Token generated:`, {
      tokenHexLength: tokenHex.length,
      expected: 150, // 75 bytes * 2
      employee_short_id,
      timestamp,
      role
    })
    
    return tokenHex
  }
  
  /**
   * Проверяет, должны ли мы использовать бинарный формат
   * (может быть переопределено через env или feature flag)
   */
  static shouldUseBinaryFormat(): boolean {
    // Проверяем переменную окружения
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      const envValue = import.meta.env.VITE_USE_BINARY_QR
      if (envValue !== undefined) {
        return envValue === 'true' || envValue === true
      }
    }
    
    // По умолчанию: используем бинарный формат (новый)
    return true
  }

  /**
   * Генерирует TOTP+HMAC токен (16 байт, новый формат)
   * 
   * @param employeeUuid UUID сотрудника
   * @param role Роль сотрудника
   * @param privateKeyHex Приватный ключ (не используется для TOTP)
   * @param shortId Short ID сотрудника (если известен)
   * @returns Hex-строка TOTP токена (32 символа)
   */
  static async generateTOTPToken(
    employeeUuid: string,
    role: 'worker' | 'admin',
    _privateKeyHex: string, // не используется для TOTP
    shortId?: number
  ): Promise<string> {
    // Импортируем TOTP функции
    const { generateTOTPToken } = await import('../utils/totpToken')
    const { getEmployeeTOTPSecret } = await import('./totpService')
    
    // Получаем TOTP secret и short_id
    const { secret, shortId: fetchedShortId } = await getEmployeeTOTPSecret(employeeUuid)
    const finalShortId = shortId ?? fetchedShortId
    
    console.log('[CryptoService] Generating TOTP token:', {
      shortId: finalShortId,
      role,
      tokenFormat: 'TOTP+HMAC (16 bytes)'
    })
    
    // Генерируем TOTP токен
    const tokenHex = await generateTOTPToken(finalShortId, secret, role)
    
    console.log('[CryptoService] TOTP token generated:', {
      length: tokenHex.length,
      expected: 32,
      preview: tokenHex.substring(0, 16) + '...'
    })
    
    return tokenHex
  }

  /**
   * Определяет формат токена для генерации
   */
  static getTokenFormat(): 'totp' | 'binary' | 'jwt' {
    // Используем TOTP формат (16 байт)
    return 'totp'
  }
}

